'use client';

import { useQuery } from '@tanstack/react-query';
import { getCookies } from 'cookies-next/client';
import { usePathname, useRouter } from 'next/navigation';
import { useMemo, useRef, useState } from 'react';
import { maxAge, minAge, vulnerableCountryIsoCodes } from '@sola/contentful-model/lib/sac-values.ts';
import { childSearch, getRandomChildren } from '@sola/proxy-sdk/children.ts';
import type { ChildAvailabilityItem } from '@sola/schema/dotcom/children/child.ts';
import { ChildGenderEnum } from '@sola/schema/dotcom/children/childUtilityInterfaces.ts';
import type { SacFilterProps } from '@sola/schema/lib/schema.ts';
import type { SacQueryParams } from '../../utils/query-params.sac.tsx';
import { SacFiltersEnum } from '../../utils/query-params.sac.tsx';
const defaultLimit = 12;
const maxPages = 9;
const SAC_SEED_KEY = 'sacRichMixSeed';
/** Max value for a 32-bit signed integer — safe upper bound for backend seed params. */
const maxInt32 = 2_147_483_647;

/** Get or create a stable integer seed for the current user. */
const getOrCreateSeed = (): number => {
  try {
    const stored = localStorage.getItem(SAC_SEED_KEY);
    if (stored) {
      const {
        seed
      } = JSON.parse(stored);
      if (typeof seed === 'number') {
        return seed;
      }
    }
  } catch {
    // Malformed or inaccessible localStorage — generate fresh
  }
  const seed = Math.floor(Math.random() * maxInt32);
  try {
    localStorage.setItem(SAC_SEED_KEY, JSON.stringify({
      seed
    }));
  } catch {
    // Storage full or unavailable — seed still works for this session
  }
  return seed;
};

/** Narrow countries to HVC overlap when the vulnerable filter is active. */
export const resolveFilteredCountries = (countries: string[] | null, isVulnerable: boolean | undefined): string[] | undefined => {
  if (!isVulnerable) {
    return countries ? [...countries] : undefined;
  }
  const hvc = vulnerableCountryIsoCodes as readonly string[];
  const selected = countries ? [...countries] : undefined;
  if (selected?.length) {
    const matched = selected.filter(c => hvc.includes(c));
    return matched.length > 0 ? matched : selected;
  }
  return [...vulnerableCountryIsoCodes];
};

/** Normalize gender — treat "Either" as unfiltered. */
export const normalizeGender = (gender?: string | null): string | null => gender && gender !== ChildGenderEnum.Either ? gender : null;

/** Map chip filter strings → boolean flags. */
export const resolveChipFilters = (filters?: string[]) => ({
  disability: filters?.includes(SacFiltersEnum.enum.hasDisabilities) || undefined,
  isInHivAffectedArea: filters?.includes(SacFiltersEnum.enum.affectedHiv) || undefined,
  isOrphan: filters?.includes(SacFiltersEnum.enum.orphanedChild) || undefined,
  isVulnerable: filters?.includes(SacFiltersEnum.enum.vulnerableExploitation) || undefined
});
export type ResolvedSacFilters = {
  gender: string | null;
  ageMin: number | null;
  ageMax: number | null;
  countries: string[] | null;
  birthDay: string | null;
  birthMonth: string | null;
  disability: boolean | undefined;
  isInHivAffectedArea: boolean | undefined;
  isOrphan: boolean | undefined;
  isVulnerable: boolean | undefined;
  limit: number;
};

/** True when any filter differs from the default/empty state. */
export const hasCriteria = (f: ResolvedSacFilters): boolean => !!(f.gender && f.gender !== ChildGenderEnum.Either || (f.ageMin && f.ageMax ? f.ageMin !== minAge || f.ageMax !== maxAge : f.ageMin || f.ageMax) || (f.countries?.length ?? 0) > 0 || f.birthDay || f.birthMonth || f.isInHivAffectedArea || f.disability || f.isOrphan || f.isVulnerable);

/** Map CMS props → ResolvedSacFilters (non-live embeds). */
export const resolveFiltersFromProps = (filters: SacFilterProps): ResolvedSacFilters => ({
  gender: normalizeGender(filters.gender),
  ageMin: filters.ageMin ?? null,
  ageMax: filters.ageMax ?? null,
  countries: filters.countries ?? null,
  birthDay: null,
  birthMonth: null,
  disability: filters.disability || undefined,
  isInHivAffectedArea: filters.hiv || undefined,
  isOrphan: filters.orphan || undefined,
  isVulnerable: filters.highlyVulnerable || undefined,
  limit: filters.limit ?? defaultLimit
});

/** Map URL query params → ResolvedSacFilters (live SAC page). */
export const resolveFiltersFromQueryParams = (sacFilters: SacQueryParams, limit = defaultLimit): ResolvedSacFilters => ({
  gender: normalizeGender(sacFilters.gender),
  ageMin: sacFilters.ageMin ?? null,
  ageMax: sacFilters.ageMax ?? null,
  countries: sacFilters.countries?.filter(Boolean) ?? null,
  birthDay: sacFilters.birthDay ? String(sacFilters.birthDay) : null,
  birthMonth: sacFilters.birthMonth ? String(sacFilters.birthMonth) : null,
  ...resolveChipFilters(sacFilters.filters),
  limit
});
export const useSacList = (resolved: ResolvedSacFilters, isDynamic: boolean) => {
  const {
    gender,
    ageMin,
    ageMax,
    countries,
    birthDay,
    birthMonth,
    disability,
    isInHivAffectedArea,
    isOrphan,
    isVulnerable,
    limit
  } = resolved;
  const filteredCountries = useMemo(() => resolveFilteredCountries(countries, isVulnerable), [countries, isVulnerable]);
  const hasActiveCriteria = hasCriteria(resolved);
  const [page, setPage] = useState(0);
  const pathname = usePathname();
  const router = useRouter();
  const cookieStore = getCookies();
  const resetFilters = () => router.push(pathname);

  /** Query key for TanStack Query — structural comparison handles caching. */
  const queryKey = ['sacChildren', gender, ageMin, ageMax, countries, birthDay, birthMonth, disability, isInHivAffectedArea, isOrphan, isVulnerable, limit];
  const {
    data: storedChildren = [],
    isLoading
  } = useQuery({
    queryKey,
    queryFn: async () => {
      const referrer = String(cookieStore?.referrer);
      const seed = getOrCreateSeed();
      const results = hasActiveCriteria ? await childSearch({
        take: limit * 10,
        skip: 0,
        gender: gender as ChildGenderEnum,
        ageMin,
        ageMax,
        countries: isVulnerable ? filteredCountries : countries,
        birthDay,
        birthMonth,
        isOrphan,
        isInHivAffectedArea,
        disability,
        referrer
      }) : await getRandomChildren(limit * 10, seed);
      return results?.children as ChildAvailabilityItem[] || [];
    }
  });

  // Reset page when filters change (queryKey identity changes).
  // Uses a stringified comparison to avoid thrashing if a caller passes a
  // fresh array reference with identical contents.
  const queryKeyStr = JSON.stringify(queryKey);
  const prevQueryKeyStrRef = useRef(queryKeyStr);
  if (prevQueryKeyStrRef.current !== queryKeyStr) {
    prevQueryKeyStrRef.current = queryKeyStr;
    if (page !== 0) {
      setPage(0);
    }
  }
  const children = storedChildren.slice(0, (page + 1) * limit);
  const canLoadMore = storedChildren.length > 0 && page < maxPages && storedChildren.length > (page + 1) * limit;
  const incrementPage = () => {
    if (page < maxPages) {
      setPage(page + 1);
    }
  };
  return {
    isLoading,
    children,
    showRichMix: !hasActiveCriteria,
    showLoadMoreButton: isDynamic && canLoadMore,
    incrementPage,
    resetFilters,
    limit
  };
};